import { assign, createMachine } from 'xstate';
import { Credentials, CredentialsEntity, CredentialsStatus } from '../../models/credentials';
import { ApplicationErrorPayload } from '../../stores/app-state-store';
import { createCredentialsPollerMachine as createCredentialsPollerMachineImport } from './credentialsPollerMachine';

export type ThirdPartyRedirectMachineContext = {
  credentials: CredentialsEntity;
  error?: ApplicationErrorPayload;
};

type ThirdPartyRedirectMachineEvents =
  | { type: 'EVENT_GO_TO_REDIRECT_PENDING' }
  | { type: 'EVENT_GO_BACK' };

type ThirdPartyRedirectMachineServices = {
  pollCredentials: any;
};

type TestingProps = {
  createCredentialsPollerMachine?: typeof createCredentialsPollerMachineImport;
  initialState?: string;
};

export type ThirdPartyRedirectMachineProps = {
  initialCredentials: CredentialsEntity<CredentialsStatus.AWAITING_THIRD_PARTY_APP_AUTHENTICATION>;
} & TestingProps;

export const createThirdPartyRedirectMachine = ({
  initialCredentials,
  createCredentialsPollerMachine = createCredentialsPollerMachineImport,
  initialState = 'processAutoRedirect',
}: ThirdPartyRedirectMachineProps) =>
  createMachine(
    {
      id: 'third-party-redirect-machine',
      predictableActionArguments: true,
      schema: {
        context: {} as ThirdPartyRedirectMachineContext,
        events: {} as ThirdPartyRedirectMachineEvents,
        services: {} as ThirdPartyRedirectMachineServices,
      },
      tsTypes: {} as import('./thirdPartyRedirectMachine.typegen').Typegen0,
      context: {
        credentials: initialCredentials,
      },
      initial: initialState,
      states: {
        processAutoRedirect: {
          on: {
            EVENT_GO_TO_REDIRECT_PENDING: { target: 'processRedirectPending' },
          },
          after: {
            1000: {
              target: 'uiPollingOpenRedirect',
              description:
                'Give the auto redirect time to happen in the view component before transitioning',
            },
          },
        },
        uiPollingOpenRedirect: {
          invoke: {
            id: 'poll-credentials',
            src: 'pollCredentials',
            onDone: [
              { cond: 'isCredentialsStatusUpdated', target: 'done', actions: 'setCredentials' },
              {
                cond: 'hasCredentialsError',
                target: 'error',
                actions: 'setCredentials',
              },
              { target: 'uiPollingOpenRedirect', actions: 'setCredentials' },
            ],
            onError: { target: 'error', actions: 'setCredentialsFetchingError' },
          },
          on: {
            EVENT_GO_TO_REDIRECT_PENDING: { target: 'processRedirectPending' },
            EVENT_GO_BACK: {
              target: 'processGoBack',
            },
          },
        },
        processRedirectPending: {
          after: {
            2000: {
              target: 'uiPollingRedirectPending',
              description:
                'Give the redirect time to happen in the view component before transitioning',
            },
          },
        },
        uiPollingRedirectPending: {
          invoke: {
            id: 'poll-credentials',
            src: 'pollCredentials',
            onDone: [
              { cond: 'isCredentialsStatusUpdated', target: 'done', actions: 'setCredentials' },
              {
                cond: 'hasCredentialsError',
                target: 'error',
                actions: 'setCredentials',
              },
              { target: 'uiPollingRedirectPending', actions: 'setCredentials' },
            ],
            onError: { target: 'error', actions: 'setCredentialsFetchingError' },
          },
          on: {
            EVENT_GO_BACK: {
              target: 'processGoBack',
            },
          },
        },
        processGoBack: { type: 'final' },
        done: { type: 'final' },
        error: { type: 'final' },
      },
    },
    {
      services: {
        pollCredentials: ({ credentials }) => createCredentialsPollerMachine({ credentials }),
      },
      actions: {
        setCredentials: assign({ credentials: (_, { data }) => data as CredentialsEntity }),
        setCredentialsFetchingError: assign({
          error: (_, { data }) => data as ApplicationErrorPayload,
        }),
      },
      guards: {
        isCredentialsStatusUpdated: (_, { data }) =>
          Credentials.isUpdated(data as CredentialsEntity),
        hasCredentialsError: (_, { data }) => Credentials.hasError(data as CredentialsEntity),
      },
    }
  );
